import { createRequests } from 'query/http';

const STORAGE_KEY = 'affiliateBrandingCache';
export const BRANDING_UPDATED_EVENT = 'affiliate-branding-updated';
const unauthClient = createRequests({
    credentials: 'omit', authorization: false, csrf: false, handleErrors: false, unwrap: false,
    headers: { Accept: 'application/json' },
});

function parseBrandingButtons(raw: any): Record<string, string> | null {
    if (!raw) return null;
    try {
        return typeof raw === 'string' ? JSON.parse(raw) : raw;
    } catch {
        // If parsing fails but a value exists, keep the raw payload so we don't erase it.
        return typeof raw === 'object' ? raw : { raw };
    }
}

export type AffiliateBranding = {
    affiliate_business_id: string;
    branding_logo: string | null;
    branding_logo_dark: string | null;
    branding_images?: Record<string, any> | null;
    branding_buttons?: Record<string, string> | null;
    fetched_at: number;
};

const logoUrlCache = new Map<string, string>();
const logoRequestCache = new Map<string, Promise<string | null>>();

export function loadAffiliateBrandingFromStorage(): AffiliateBranding | null {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as AffiliateBranding;
        return parsed;
    } catch {
        return null;
    }
}

export function saveAffiliateBrandingToStorage(data: AffiliateBranding | null) {
    if (!data) {
        localStorage.removeItem(STORAGE_KEY);
        if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent(BRANDING_UPDATED_EVENT));
        }
        return;
    }
    const existing = loadAffiliateBrandingFromStorage();
    const normalizedButtons =
        data.branding_buttons !== undefined
            ? data.branding_buttons
            : existing?.branding_buttons ?? null;
    const normalizedImages =
        data.branding_images !== undefined
            ? data.branding_images
            : existing?.branding_images ?? null;
    const payload: AffiliateBranding = {
        affiliate_business_id: String(data.affiliate_business_id || 'unknown'),
        branding_logo: data.branding_logo ?? null,
        branding_logo_dark: data.branding_logo_dark ?? null,
        branding_images: normalizedImages,
        branding_buttons: normalizedButtons,
        fetched_at: data.fetched_at || Date.now(),
    };
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
        if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent(BRANDING_UPDATED_EVENT));
        }
    } catch {
        // ignore
    }
}

export function isAffiliateBrandingStale(branding: AffiliateBranding | null, ttlMs = 6 * 60 * 60 * 1000) {
    if (!branding) return true;
    return (Date.now() - branding.fetched_at) > ttlMs;
}

export async function fetchAffiliateBranding(affiliate_business_id: string | null | undefined): Promise<AffiliateBranding | null> {
    if (!affiliate_business_id) return null;
    try {
        const cached = loadAffiliateBrandingFromStorage();
        const { data }: any = await unauthClient.query({ ...{
            params: { affiliate_business_id },
        }, url: '/api/affiliate/branding', method: 'GET' });
        let brandingImages: Record<string, any> | null = null;
        if (data?.branding_images) {
            try {
                brandingImages = typeof data.branding_images === 'string'
                    ? JSON.parse(data.branding_images)
                    : data.branding_images;
            } catch {
                brandingImages = cached?.branding_images ?? null;
            }
        } else if (cached?.branding_images) {
            brandingImages = cached.branding_images;
        }
        const brandingButtons =
            parseBrandingButtons(data?.branding_buttons) ??
            cached?.branding_buttons ??
            null;
        const branding: AffiliateBranding = {
            affiliate_business_id: String(affiliate_business_id),
            branding_logo: data?.branding_logo ?? null,
            branding_logo_dark: data?.branding_logo_dark ?? null,
            branding_images: brandingImages,
            branding_buttons: brandingButtons,
            fetched_at: Date.now(),
        };
        saveAffiliateBrandingToStorage(branding);
        return branding;
    } catch (_e) {
        return null;
    }
}

export async function fileIdToObjectUrl(fileId?: string | null): Promise<string | null> {
    if (!fileId) return null;
    const key = `file:${fileId}`;
    if (logoUrlCache.has(key)) return logoUrlCache.get(key)!;
    if (logoRequestCache.has(key)) return logoRequestCache.get(key)!;

    const req = (async () => {
        try {
            const response: any = await unauthClient.query({ ...{ responseType: 'blob' }, url: `/api/get-file-stream-by-id/${fileId}`, method: 'GET' });
            const blob = new Blob([response?.data ?? response], { type: 'image/*' });
            const url = URL.createObjectURL(blob);
            logoUrlCache.set(key, url);
            return url;
        } catch (_e) {
            return null;
        } finally {
            logoRequestCache.delete(key);
        }
    })();

    logoRequestCache.set(key, req);
    return req;
}

export async function resolveAffiliateLogoUrls(
    branding: AffiliateBranding | null,
    options?: { preferSmall?: boolean }
): Promise<{ light: string | null; dark: string | null; }> {
    if (!branding) return { light: null, dark: null };
    const preferSmall = !!options?.preferSmall;
    const images = branding.branding_images || {};

    const pickLight = () => {
        if (preferSmall) return images.branding_logo_small ?? branding.branding_logo ?? images.branding_logo ?? null;
        return branding.branding_logo ?? images.branding_logo ?? images.branding_logo_small ?? null;
    };

    const pickDark = () => {
        if (preferSmall) {
            return (
                images.branding_logo_small_dark ??
                images.branding_logo_small ??
                branding.branding_logo_dark ??
                images.branding_logo_dark ??
                branding.branding_logo ??
                images.branding_logo ??
                null
            );
        }
        return (
            branding.branding_logo_dark ??
            images.branding_logo_dark ??
            branding.branding_logo ??
            images.branding_logo ??
            images.branding_logo_small_dark ??
            images.branding_logo_small ??
            null
        );
    };

    const lightId = pickLight();
    const darkId = pickDark();

    const [light, dark] = await Promise.all([
        fileIdToObjectUrl(lightId),
        fileIdToObjectUrl(darkId),
    ]);
    return { light, dark };
}
